import { env } from 'src/config/env';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'antd';
import { CapacitorShake } from '@capgo/capacitor-shake';
import type { PluginListenerHandle } from '@capacitor/core';
import { createBugReport, reportError } from 'src/modules/logs/Sentry';
import { debounceLeading } from 'src/utils/debounce';
import { AutoFloatingPanel } from '../../designSystem/AutoFloatingPanel';
import Button from 'src/designSystem/Button';
import Input from 'src/designSystem/Input';

const DebugShakeListener = () => {
  const handler = useRef<PluginListenerHandle>(undefined);
  const [isPanelOpen, setPanelOpen] = useState(false);
  const [form] = Form.useForm<{ bugDescription: string }>();

  const listener = useMemo(
    () =>
      debounceLeading(() => {
        setPanelOpen(true);
      }, 2000),
    [],
  );

  const sendBug = async () => {
    try {
      const values = await form.validateFields();
      if (!values.bugDescription) {
        return;
      }
      await createBugReport(values.bugDescription);
      setPanelOpen(false);
      form.resetFields();
    } catch {
      // Form validation failed
    }
  };

  useEffect(() => {
    if (env.mode !== 'staging') {
      return;
    }

    if (handler.current) {
      return;
    }

    CapacitorShake.addListener('shake', listener)
      .then(_handler => {
        handler.current = _handler;
      })
      .catch(err => reportError('Could not add shake listener', err));

    return () => {
      handler.current?.remove();
      handler.current = undefined;
    };
  }, [listener]);

  return (
    <AutoFloatingPanel isOpen={isPanelOpen} onClose={() => setPanelOpen(false)}>
      <Form form={form} layout="vertical">
        <Form.Item name="bugDescription" className="mb-0">
          <Input placeholder={'Description du bug'} />
        </Form.Item>
        <Form.Item
          dependencies={['bugDescription']}
          noStyle
        >
          {({ getFieldValue }) => {
            const bugDescription = getFieldValue('bugDescription');
            return (
              <Button
                type={'primary'}
                disabled={!bugDescription}
                onClick={sendBug}
              >
                Envoyer le bug
              </Button>
            );
          }}
        </Form.Item>
      </Form>
    </AutoFloatingPanel>
  );
};

export default DebugShakeListener;
